import type {
  StrapiMagazineArticle,
  StrapiMagazineArticleCategory,
} from '~/composables/types/strapi/strapi-magazine'
import { articleGetters } from '~/utils/articleGetters'
import { articleCategoryGetters } from '~/utils/articleCategoryGetters'
import { withTrailingSlash } from 'ufo'

export interface HreflangLink {
  key: string
  rel: 'alternate'
  hreflang: string
  href: string
}

/**
 * Builds hreflang link tags for magazine article/category pages using the same
 * buildLocaleUrl logic as the locale switcher. Use with useSeoHelper's link option.
 */
export const useMagazineHreflang = (
  article: Ref<StrapiMagazineArticle | null | undefined>,
  category: Ref<StrapiMagazineArticleCategory | null | undefined>
): ComputedRef<HreflangLink[]> => {
  const { locales, defaultLocale } = useI18n()
  const { baseUrl } = useRuntimeConfig().public
  const route = useRoute()

  const toHref = (path: string) =>
    withTrailingSlash(
      `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`,
      true
    )

  const toLink = (
    key: string,
    hreflang: string,
    path: string
  ): HreflangLink => ({
    key,
    rel: 'alternate',
    hreflang,
    href: toHref(path),
  })

  return computed(() => {
    const articleValue = article.value
    const categoryValue = category.value

    if (!articleValue && !categoryValue) return []

    const { buildLocaleUrl } = articleValue
      ? useMagazineArticleLocaleSwitcher(articleValue)
      : useMagazineCategoryLocaleSwitcher(categoryValue!)

    const currentLocale = (
      articleValue
        ? articleGetters.getLocale(articleValue)
        : articleCategoryGetters.getLocale(categoryValue!)
    )?.toLowerCase()

    if (!currentLocale) return []

    const links: HreflangLink[] = locales.value.flatMap((loc) => {
      const language = loc.code.split('-').at(0)!
      const path =
        buildLocaleUrl(loc.code) ??
        (loc.code === currentLocale ? route.path : null)
      return path ? [toLink(`i18n-alt-${language}`, language, path)] : []
    })

    const xDefaultPath =
      buildLocaleUrl(defaultLocale) ??
      (currentLocale === defaultLocale ? route.path : null)
    if (xDefaultPath) {
      links.push(toLink('i18n-xd', 'x-default', xDefaultPath))
    }

    return links
  })
}
